import React, { useState, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import { Input, Modal, Tag } from 'antd'
import { Select, SelectDropDown, SelectButton } from 'components/Common'
import { PrimaryButton, OutlineButton, FieldGroup, FieldLabel } from './styles'
import { MerchantLocation } from '@slerp/controls'

interface SelectStoresModalProps {
  idPrefix: string
  isOpen: boolean
  loading?: boolean
  locations: MerchantLocation[]
  initialIds?: string[]
  disabledIds?: string[]
  hasApiKey?: boolean
  title?: string
  confirmLabel?: string
  storesLabel?: string
  onBack: () => void
  onComplete: (storeIds: string[], apiKey?: string) => void
}

const SelectStoresModal = ({
  idPrefix,
  isOpen,
  loading,
  locations,
  initialIds,
  disabledIds,
  hasApiKey,
  title = 'Select stores',
  confirmLabel = 'COMPLETE SETUP',
  storesLabel = 'Add or remove stores for this connection',
  onBack,
  onComplete
}: SelectStoresModalProps) => {
  const storesId = `${idPrefix}-stores`
  const apiKeyId = `${idPrefix}-api-key`
  const [selectedIds, setSelectedIds] = useState<string[]>(initialIds ?? [])
  const [apiKey, setApiKey] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(initialIds ?? [])
      setApiKey('')
    }
  }, [isOpen, initialIds])

  const disabledSet = useMemo(() => new Set(disabledIds), [disabledIds])
  const allOptions = useMemo(
    () =>
      locations.map((loc) => ({
        label: loc.name,
        value: loc.id,
        disabled: disabledSet.has(loc.id)
      })),
    [locations, disabledSet]
  )

  const filteredOptions = useMemo(() => {
    if (searchQuery.length < 3) return allOptions
    return allOptions.filter((o) =>
      o.label.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [allOptions, searchQuery])

  const handleSelect = (value: unknown) => {
    setSelectedIds((prev) => [...prev, value as string])
  }

  const handleDeselect = (value: unknown) => {
    setSelectedIds((prev) => prev.filter((id) => id !== (value as string)))
  }

  const handleComplete = () => {
    onComplete(selectedIds, apiKey.trim() || undefined)
  }

  const handleBack = () => {
    setSelectedIds([])
    setApiKey('')
    onBack()
  }

  const selectedStores = selectedIds
    .map((id) => locations.find((l) => l.id === id))
    .filter((l): l is MerchantLocation => Boolean(l))

  return (
    <StyledModal
      open={isOpen}
      footer={null}
      closable={false}
      width={592}
      style={{ top: 80 }}
      onCancel={handleBack}
    >
      <ModalTitle>{title}</ModalTitle>
      <ModalContent>
        <FieldGroup>
          <FieldLabel htmlFor={storesId}>{storesLabel}</FieldLabel>
          <SelectWrapper>
            {selectedIds.length > 0 && !searchQuery && (
              <SelectPlaceholder>
                {`${selectedIds.length} store${
                  selectedIds.length !== 1 ? 's' : ''
                } selected`}
              </SelectPlaceholder>
            )}
            <LocationSelect
              id={storesId}
              mode='multiple'
              placeholder='Search for store'
              value={selectedIds}
              showArrow
              filterOption={false}
              onSearch={(query) => setSearchQuery(query)}
              onSelect={(val) => {
                handleSelect(val)
                setSearchQuery('')
              }}
              onDeselect={handleDeselect}
              onDropdownVisibleChange={(open) => {
                setIsDropdownOpen(open)
                if (!open) setSearchQuery('')
              }}
              tagRender={() => <></>}
              open={isDropdownOpen}
              options={filteredOptions}
              dropdownRender={(menu) => (
                <SelectDropDown>
                  {menu}
                  <DoneRow>
                    <SelectButton onClick={() => setIsDropdownOpen(false)}>
                      DONE
                    </SelectButton>
                  </DoneRow>
                </SelectDropDown>
              )}
            />
          </SelectWrapper>
          {selectedStores.length > 0 && (
            <TagList>
              {selectedStores.map((store) => (
                <LocationTag
                  key={store.id}
                  closable={!disabledSet.has(store.id)}
                  onClose={() => handleDeselect(store.id)}
                >
                  {store.name}
                </LocationTag>
              ))}
            </TagList>
          )}
        </FieldGroup>
        {hasApiKey && (
          <FieldGroup>
            <FieldLabel htmlFor={apiKeyId}>
              API key for this connection
            </FieldLabel>
            <StyledInput
              id={apiKeyId}
              placeholder='Leave blank to keep current key'
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </FieldGroup>
        )}
      </ModalContent>
      <ModalFooter>
        <OutlineButton style={{ width: 'auto' }} onClick={handleBack}>
          BACK
        </OutlineButton>
        <PrimaryButton
          disabled={selectedIds.length === 0 || loading}
          loading={loading}
          onClick={handleComplete}
        >
          {confirmLabel}
        </PrimaryButton>
      </ModalFooter>
    </StyledModal>
  )
}

const StyledInput = styled(Input)`
  &:hover,
  &:active,
  &:focus {
    border-color: #4a4a68;
  }
`

const StyledModal = styled(Modal)`
  .ant-modal-content {
    max-height: 80vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
  .ant-modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
`

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2b2b50;
  margin-bottom: 4px;
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 12px 0 24px 0;
`

const SelectWrapper = styled.div`
  position: relative;
`

const SelectPlaceholder = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(0, 0, 0, 0.25);
  font-size: inherit;
  line-height: inherit;
  pointer-events: none;
  z-index: 1;
`

const LocationSelect = styled(Select)`
  width: 100%;

  .ant-select-selection-overflow {
    display: none;
  }
` as unknown as typeof Select

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 8px;
`

const LocationTag = styled(Tag)`
  background-color: #e7e5e9;
  color: #8c8ca1;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;

  .ant-tag-close-icon {
    color: #8c8ca1;
    margin-left: 4px;
  }
`

const DoneRow = styled.div`
  padding: 8px 12px;
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  > button {
    width: min-content;
  }
`

export default SelectStoresModal
